import type { AdminGymExerciseGroup, AdminGymFoodGroup } from '@steado/admin-contract/gym-catalog';
import { useQuery } from '@tanstack/react-query';

import { Empty, Panel, TabSkeleton } from '@/components/panel/panels';
import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { BadgeCell, TextCell } from '@/components/table/cells';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/lib/auth/auth-context';
import { formatRelative } from '@/lib/format';

import { GymGroupActions } from './GymGroupActions';
import { gymExerciseGroupsQueryOptions, gymFoodGroupsQueryOptions } from './gym-catalog-query';

export interface GymGroupsProps {
  readonly onMake: (name: string, ids: readonly string[]) => void;
}

const FOOD_COLUMNS: readonly TableColumn[] = [
  { key: 'gym', label: 'Gym', width: '1.2fr' },
  { key: 'serving', label: 'Serving', width: '0.8fr' },
  { key: 'per100', label: 'Per 100 g (kcal · P · C · F)', width: '1.3fr' },
  { key: 'diet', label: 'Diet and allergens', width: '1fr' },
  { key: 'use', label: 'In use', width: '0.8fr' },
];

const EXERCISE_COLUMNS: readonly TableColumn[] = [
  { key: 'gym', label: 'Gym', width: '1.1fr' },
  { key: 'kind', label: 'Kind and level', width: '0.9fr' },
  { key: 'safety', label: 'Loads · position · pregnancy answers', width: '1.6fr' },
  { key: 'demo', label: 'Demo', width: '0.6fr' },
  { key: 'use', label: 'In use', width: '0.8fr' },
];

/** "Adopted 3 of 4": how much of this gym's past additions Steado took. */
function trust(item: { gymTrust: { adopted: number; decided: number } }): string {
  const { adopted, decided } = item.gymTrust;
  return decided === 0 ? 'First item reviewed' : `Adopted ${String(adopted)} of ${String(decided)}`;
}

function use(item: { usage: { membersInPlans: number; logs30d: number } }) {
  return (
    <TextCell
      key="use"
      value={`${String(item.usage.membersInPlans)} in plans`}
      sub={`${String(item.usage.logs30d)} logs, 30 days`}
    />
  );
}

/** Steado's own by much the same name: the first thing to rule out. */
function Similar({
  similar,
}: {
  readonly similar: readonly { id: string; name: string; sameName: boolean }[];
}) {
  if (similar.length === 0) return null;
  return (
    <p className="flex flex-wrap items-center gap-1.5 text-[12.5px] text-muted-foreground">
      Steado may already have:
      {similar.map((s) => (
        <Badge key={s.id} tone={s.sameName ? 'warn' : 'mute'} size="sm">
          {s.name}
          {s.sameName ? ' (same name)' : ''}
        </Badge>
      ))}
    </p>
  );
}

function groupDescription(group: {
  gyms: number;
  usage: { membersInPlans: number; logs30d: number };
}): string {
  return `${String(group.gyms)} ${group.gyms === 1 ? 'gym' : 'gyms'} · ${String(group.usage.membersInPlans)} members have it in a plan · ${String(group.usage.logs30d)} logs in 30 days`;
}

/** The gyms' foods waiting for a decision, one panel per name, most-used first. */
export function GymFoodGroups({ onMake }: GymGroupsProps) {
  const { api } = useAuth();
  const groups = useQuery(gymFoodGroupsQueryOptions(api));
  if (groups.isPending) return <TabSkeleton />;
  if (groups.isError) {
    return (
      <Alert role="alert" tone="err">
        <AlertDescription>{groups.error.message}</AlertDescription>
      </Alert>
    );
  }
  if (groups.data.length === 0) {
    return <Empty title="Nothing waiting" body="Every food a gym added has been decided." />;
  }
  return (
    <div className="flex flex-col gap-3">
      {groups.data.map((group: AdminGymFoodGroup) => {
        const rows: readonly TableRow[] = group.items.map((item) => ({
          id: item.id,
          cells: [
            <TextCell key="gym" value={item.gymName} sub={`${item.name} · ${trust(item)}`} weight="medium" />,
            <TextCell
              key="serving"
              value={
                item.serving === null ? '100 g' : `${item.serving.label}, ${String(item.serving.grams)} g`
              }
              sub={item.createdByName === null ? formatRelative(item.createdAt) : `by ${item.createdByName}`}
            />,
            <TextCell
              key="per100"
              mono
              value={`${String(Math.round(item.kcalPer100g))} · ${String(item.proteinGPer100g)} · ${String(item.carbsGPer100g)} · ${String(item.fatGPer100g)}`}
              sub={item.fibreGPer100g === null ? null : `fibre ${String(item.fibreGPer100g)}`}
            />,
            <span key="diet" className="flex flex-col gap-1">
              <BadgeCell label={item.dietClass.replace(/_/g, ' ')} tone="mute" />
              <span className="text-xs text-muted-foreground">
                {item.allergens.length === 0 ? 'No allergens' : item.allergens.join(', ')}
                {item.hasOnionGarlic ? ' · onion or garlic' : ''}
                {item.jainExcluded ? ' · not Jain' : ''}
              </span>
            </span>,
            use(item),
          ],
        }));
        return (
          <Panel
            key={group.key}
            title={group.name}
            description={`${groupDescription(group)}${group.items.length > 1 ? ` · spread ${String(group.spread.kcal)} kcal, ${String(group.spread.proteinG)} g protein per 100 g` : ''}`}
            action={
              <GymGroupActions
                kind="food"
                name={group.name}
                ids={group.items.map((i) => i.id)}
                onMake={onMake}
              />
            }
            bleed
          >
            <div className="flex flex-col gap-2 px-[18px] pb-3">
              <Similar similar={group.similar} />
            </div>
            <DataTable
              caption={`${group.name}, by gym`}
              columns={FOOD_COLUMNS}
              rows={rows}
              isLoading={false}
              emptyTitle=""
              emptyBody=""
            />
          </Panel>
        );
      })}
    </div>
  );
}

/** The gyms' exercises waiting for a decision, one panel per name, most-used first. */
export function GymExerciseGroups({ onMake }: GymGroupsProps) {
  const { api } = useAuth();
  const groups = useQuery(gymExerciseGroupsQueryOptions(api));
  if (groups.isPending) return <TabSkeleton />;
  if (groups.isError) {
    return (
      <Alert role="alert" tone="err">
        <AlertDescription>{groups.error.message}</AlertDescription>
      </Alert>
    );
  }
  if (groups.data.length === 0) {
    return <Empty title="Nothing waiting" body="Every exercise a gym added has been decided." />;
  }
  return (
    <div className="flex flex-col gap-3">
      {groups.data.map((group: AdminGymExerciseGroup) => {
        const rows: readonly TableRow[] = group.items.map((item) => ({
          id: item.id,
          cells: [
            <TextCell key="gym" value={item.gymName} sub={`${item.name} · ${trust(item)}`} weight="medium" />,
            <TextCell
              key="kind"
              value={`${item.category} · ${item.level}`}
              sub={item.holdSeconds === null ? 'reps' : `held ${String(item.holdSeconds)} s`}
            />,
            <TextCell
              key="safety"
              value={`${item.bodyAreas.length === 0 ? 'No joints' : item.bodyAreas.join(', ').replace(/_/g, ' ')} · ${(item.position ?? 'not said').replace(/_/g, ' ')}`}
              sub={[
                item.highImpact ? 'high impact' : 'low impact',
                item.trunkFlexion ? 'bends the trunk' : null,
                item.avoidInPregnancy ? 'twist, backbend or arm balance' : null,
              ]
                .filter((part) => part !== null)
                .join(' · ')}
            />,
            <TextCell
              key="demo"
              value={
                item.demoUrl !== null
                  ? 'GIF'
                  : item.imageUrls.length > 0
                    ? `${String(item.imageUrls.length)} photos`
                    : 'None'
              }
              sub={item.steps.length === 0 ? 'no steps' : `${String(item.steps.length)} steps`}
            />,
            use(item),
          ],
        }));
        return (
          <Panel
            key={group.key}
            title={group.name}
            description={groupDescription(group)}
            action={
              <GymGroupActions
                kind="exercise"
                name={group.name}
                ids={group.items.map((i) => i.id)}
                onMake={onMake}
              />
            }
            bleed
          >
            <div className="flex flex-col gap-2 px-[18px] pb-3">
              <Similar similar={group.similar} />
            </div>
            <DataTable
              caption={`${group.name}, by gym`}
              columns={EXERCISE_COLUMNS}
              rows={rows}
              isLoading={false}
              emptyTitle=""
              emptyBody=""
            />
          </Panel>
        );
      })}
    </div>
  );
}
